export interface IFilterableItem {
  tags?: { title: string }[];
  description?: string;
  category?: string;
  categories?: string[];
  titleChunks?: string[] | null;
}

const isTagMatch = <T extends IFilterableItem>(query: string, item: T) =>
  item?.tags?.some((tag) => tag.title.toLowerCase().includes(query.toLowerCase()));

const isDescriptionMatch = <T extends IFilterableItem>(query: string, item: T) =>
  item.description?.toLowerCase().includes(query.toLowerCase());

const isCategoryMatch = <T extends IFilterableItem>(categoryName: string, item: T) => {
  if (!item.categories) {
    return item?.category?.toLowerCase() === categoryName.toLowerCase();
  }

  return item?.categories?.some((category) => category.toLowerCase() === categoryName.toLowerCase());
};

const isTitleMatch = <T extends IFilterableItem>(query: string, item: T) =>
  item.titleChunks?.join(' ').toLowerCase().includes(query.toLowerCase());

const filterByCategory = <T extends IFilterableItem>(categoryName: string, list: T[]) =>
  list.filter((item) => isCategoryMatch(categoryName, item));

const filterByTags = <T extends IFilterableItem>(tags: string, list: T[]) =>
  list.filter((item) => item?.tags?.map((tag) => tag.title).some((tag) => tags.split(',').includes(tag)));

const searchByDescription = <T extends IFilterableItem>(query: string, list: T[]) =>
  list.filter((item) => isDescriptionMatch(query, item));

const searchByTag = <T extends IFilterableItem>(query: string, list: T[]) =>
  list.filter((item) => isTagMatch(query, item));

const searchByTitle = <T extends IFilterableItem>(query: string, list: T[]) =>
  list.filter((item) => isTitleMatch(query, item));

const searchByAllAttributes = <T extends IFilterableItem>(query: string, list: T[]) =>
  list.filter((item) => isDescriptionMatch(query, item) || isTitleMatch(query, item));

export const filterService = {
  filterByCategory,
  filterByTags,
  searchByDescription,
  searchByTag,
  searchByTitle,
  searchByAllAttributes,
};
